import { useEffect, useState } from "react"
import { Fragment } from "react/jsx-runtime"

import { useTranslation } from "react-i18next"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useSelector } from "react-redux"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"

import * as Yup from "yup"
import { CircularProgress, Grid } from "@mui/material"
import { Button, NotificationUtils } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { FormikProvider, useFormik } from "formik"
import type { OpenAPIV3 } from "openapi-types"

import { KonanAPI } from "../../../services/KonanAPI"
import type { RootState } from "../../../store/ReduxStore"
import type { TestPrediction, baseErrorType } from "../../../types/custom/projects"
import type { ModelSettingsSetRequest } from "../../../types/generated/api/ModelSettingsSetRequest"
import type { TestPredictionResponse } from "../../../types/generated/api/TestPredictionResponse"
import { extractPredictionKeysFromOpenapi } from "../../../utils/modelDetailsHelpers"
import type { ConfigMode } from "../Interfaces"
import { ClassificationHistoryView } from "./components/ClassificationHistoryView/ClassificationHistoryView"
import { ConfigurationsForm, handleSubmit as getConfiguration } from "./components/ConfigurationsForm"
import { DryRunForm } from "./components/DryRunForm"

import styles from "./ClassificationModelConfig.module.scss"

/**
 * ClassificationModelConfig component
 *
 * This component is used to handle the configuration of a classification model
 * It contains two main parts:
 * - The first part is a form to input the request body and execute the request
 * - The second part is another form to input the configuration of the model
 *
 * @param {Object} props - The component props
 * @param {boolean} props.mode - Whether to show the history view or not
 *
 * @returns {ReactElement} - The ClassificationModelConfig component
 */
export function ClassificationModelConfig(props: Readonly<{ mode: ConfigMode }>): React.ReactElement {
  const { mode } = props
  const { t } = useTranslation("ModelConfigurations")

  const { id: projectId } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const currentModel = useSelector((state: RootState) => state.models.currentModel)

  const [activeStep, setActiveStep] = useState<number>(0)
  const [requestJsonError, setRequestJsonError] = useState<boolean>(false)

  // Configuration form validation schema object
  const configurationValidationSchema = Yup.object({
    target: Yup.string().required(t("validation.required")),
    lowerLimit: Yup.number().required(t("validation.required")),
    upperLimit: Yup.number().required(t("validation.required")),
    step: Yup.number(),
  })

  const predictionsTest = useMutation<AxiosResponse<TestPredictionResponse>, AxiosError<baseErrorType>, TestPrediction>(
    ({ model_uuid, requestJson, skip_config, abortSignal }) =>
      KonanAPI.sendModelPredictionTest(model_uuid, requestJson, skip_config, abortSignal),
  )

  const configurationMutation = useMutation<AxiosResponse, AxiosError<baseErrorType>, ModelSettingsSetRequest>(
    KonanAPI.createModelConfiguration,
    {
      onSuccess: () => {
        NotificationUtils.toast(t("notifications.configurationSuccess"), { severity: "success" })
        navigate(
          `/projects/${projectId}/decision-engines${searchParams.get("model_id") ? `?model_id=${searchParams.get("model_id")}` : ""}`,
        )
      },
    },
  )

  const DryRunFormik = useFormik({
    initialValues: {
      request: "{}",
    },
    validationSchema: Yup.object({
      request: Yup.string().required(t("validation.requestBodyRequired")),
    }),
    onSubmit: async (values) => {
      try {
        const requestJson = JSON.parse(values.request)
        setRequestJsonError(false)
        try {
          await predictionsTest.mutateAsync({
            model_uuid: currentModel?.uuid ?? "",
            requestJson: {
              ...requestJson,
            },
            skip_config: true,
          })
        } catch (e) {
          console.warn(e)
        }
      } catch (e) {
        setRequestJsonError(true)
      }
    },
  })

  // Fetch project docs using project ID in route in case no model selected
  const { isFetching: isLoadingProjectDocs } = useQuery<AxiosResponse<OpenAPIV3.Document>, AxiosError<baseErrorType>>(
    ["project-docs"],
    () => KonanAPI.fetchProjectDocs(projectId as string),
    {
      retry: false,
      enabled: !!currentModel,
      onSuccess: (response) => {
        setRequestField(response)
      },
    },
  )

  // Fetch model docs using model ID in route in case a model selected
  const { isFetching: isLoadingOpenApiData } = useQuery<AxiosResponse<OpenAPIV3.Document>, AxiosError<baseErrorType>>(
    ["model-docs", currentModel?.uuid, searchParams.get("model_id")],
    () => KonanAPI.fetchModelDocs(currentModel?.uuid ?? ""),
    {
      retry: false,
      enabled: !!currentModel,
      onSuccess: (response) => {
        setRequestField(response)
      },
    },
  )

  // Model configuration form control
  const configurationFormik = useFormik({
    initialValues: {
      dataType: "",
      reDataType: "",
      target: "",
      lowerLimit: 0,
      upperLimit: 100,
      step: 1,
      decimalNumCount: 0,
    },
    validationSchema: configurationValidationSchema,
    onSubmit: async () => {
      const requestObject = getConfiguration()

      requestObject?.settings?.forEach((setting) => {
        if (setting.type === t("dataTypes.label") && !setting.label) {
          NotificationUtils.toast(t("notifications.addLabelsToRange"), { severity: "error" })
          throw new Error(t("notifications.addLabelsToRange"))
        }
      })

      await configurationMutation.mutateAsync(requestObject).then(async () => {
        queryClient.invalidateQueries("activeConfig")
        await queryClient.invalidateQueries("model-configurations")

        // waiting to avoid flickering
        setTimeout(() => {
          DryRunFormik.resetForm()
          configurationFormik.resetForm()

          predictionsTest.reset()
          configurationMutation.reset()
        }, 200)
      })
    },
  })

  const TestResponse = predictionsTest.isError
    ? predictionsTest.error?.response?.data.ml_service_output
    : predictionsTest.data?.data.output

  const responseData = !TestResponse ? "// Execute to see response" : JSON.stringify(TestResponse, null, "  ")

  const disableExecute =
    !(DryRunFormik.isValid && DryRunFormik.dirty) || predictionsTest.isLoading || isLoadingOpenApiData || !currentModel

  // configuration steps with action buttons, description, etc...
  const steps = [
    {
      content: t("steps.step1.content"),
      btnText: t("steps.step1.btnText"),
      component: (
        <FormikProvider value={DryRunFormik}>
          <DryRunForm
            response={responseData}
            requestErrorMessage={
              requestJsonError ? t("errors.requestNotJson") : (predictionsTest.error?.response?.data.details as string)
            }
            isRequestError={requestJsonError}
            isMutationError={predictionsTest.isError}
            responseStatusCode={!requestJsonError ? predictionsTest.error?.response?.status : undefined}
            isLoadingRequest={currentModel ? isLoadingOpenApiData : isLoadingProjectDocs}
            enableSideBar={true}
          />
        </FormikProvider>
      ),
    },
    {
      name: t("steps.step1.btnText"),
      content: t("steps.step2.content"),
      btnText: configurationMutation.isLoading ? (
        <CircularProgress color="inherit" size={20} />
      ) : (
        t("steps.step2.btnText")
      ),
      component: (
        <FormikProvider value={configurationFormik}>
          <ConfigurationsForm
            responseJSON={predictionsTest.data?.data.output as Record<string, string>}
            modelId={currentModel?.uuid ?? ""}
            errorMessage={configurationMutation.error?.response?.data.details}
            invalidateMutation={() => configurationMutation.reset()}
          />
        </FormikProvider>
      ),
    },
  ]

  /**
   * generic click actions function to be called when needed
   * to make it easier to than using switch cases and if conditions
   * @return  {void}
   */
  const clickActions = (): void => {
    switch (activeStep) {
      case 0:
        setActiveStep((prev) => ++prev)
        break
      case 1:
        configurationFormik.submitForm()
        break
      default:
        break
    }
  }

  /**
   * stepper helper function
   * to help disable the next btn when needed in the generic project flow
   * @return  {boolean}
   */
  const shouldDisable = (): boolean => {
    switch (activeStep) {
      case 0:
        return !predictionsTest.isSuccess
      case 1:
        return !(configurationFormik.isValid && configurationFormik.dirty) || configurationMutation.isLoading
      default:
        return false
    }
  }

  const setRequestField = (response: AxiosResponse<OpenAPIV3.Document<object>, unknown> | undefined): void => {
    if (response?.data) {
      try {
        const extractedObjectJSON = JSON.stringify(extractPredictionKeysFromOpenapi(response?.data), null, 2)
        DryRunFormik.setFieldValue("request", extractedObjectJSON)
      } catch (_) {
        DryRunFormik.setFieldValue("request", "{}")
      }
    }
  }

  // this useEffect is used to reset the config steps when the model changes from the header
  useEffect(() => {
    setActiveStep(0)
  }, [currentModel])

  return (
    <Fragment>
      {mode === "view" ? (
        <ClassificationHistoryView />
      ) : (
        <div className={styles.flowContainer}>
          <Grid container item xs={12}>
            {steps[activeStep]?.component}
          </Grid>

          <div className={styles.actionsBtnsContainer}>
            {activeStep === 0 && (
              <Button
                variant="secondary"
                disabled={disableExecute}
                onClick={() => {
                  DryRunFormik.submitForm()
                }}
                id="execute"
                isLoading={predictionsTest.isLoading}
              >
                {t("buttons.execute")}
              </Button>
            )}
            {/* Add skip button to the first step to handle when the api request fails */}
            {activeStep === 0 && (
              <Button
                variant="secondary"
                disabled={disableExecute}
                onClick={() => setActiveStep((prev) => ++prev)}
                id="skip"
              >
                {t("buttons.skip")}
              </Button>
            )}
            {activeStep === 1 && (
              <Button variant="secondary" onClick={() => setActiveStep((prev) => --prev)} id="retest">
                {t("buttons.back")}
              </Button>
            )}

            <Button onClick={clickActions} disabled={shouldDisable()} variant="primary">
              {steps[activeStep]?.btnText}
            </Button>
          </div>
        </div>
      )}
    </Fragment>
  )
}
